import React, { useEffect, useRef, useState } from "react"
import { BiPencil } from "react-icons/bi"
import { FaUserCircle } from "react-icons/fa"
import { IoChevronBack } from "react-icons/io5"
import { useLocation, useNavigate } from "react-router-dom"

import { useEmailVerification } from "@fuse/hooks/useEmailVerification"
import BackgroundWrapper from "@fuse/sharedComponents/backgroundWrapper"
import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import { blurKeys } from "@fuse/sharedComponents/blurWrapper/blurKeysList"
import Button from "@fuse/sharedComponents/buttons/SolidBtn"
import LoadingSpinner from "@fuse/sharedComponents/loader/LoadingSpinner"
import StripeConnectionModal from "@fuse/sharedComponents/stripeConnectionModal"
import {
  showErrorToast,
  showSuccessToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import { EFeatureFlags } from "@letshang/constants"
import { get } from "lodash-es"
import moment from "moment"
import { useRecoilValue, useSetRecoilState } from "recoil"
import { ROUTES } from "src/App"

import { defaultUser, userState } from "@fuse/recoil/signup"
import { stripeUrlState } from "@fuse/recoil/stripe"

import { auth } from "@firebaseConfig/clientApp"

import { useProcessFeatureFlags } from "@settingsAlias/featureFlags"

import { getUserInfo, onboardUserToStripe } from "@api/firebaseWrappedApis"
import { updateUserDetails } from "@api/firebaseWrappedApis"

import { isDev } from "@fuse/utils/DeviceDection"
import { uploadImage } from "@fuse/utils/Firebase"

import { CustomDateController } from "@fuse/pages/eventSignUp/BuyTickets/FillQuestionnaire"

const backgroundImage = "/assets/alpha-event-not-found.svg"

const CompleteProfilePage = () => {
  const navigate = useNavigate()
  const user = useRecoilValue(userState)
  const setUser = useSetRecoilState(userState)
  const stripeUrl = useRecoilValue(stripeUrlState)
  const setStripeUrl = useSetRecoilState(stripeUrlState)
  const { eventId } = useLocation().state || { eventId: "" }

  // Feature flag for flexible event creation
  const flexibleEventCreationEnabled = useProcessFeatureFlags(
    EFeatureFlags.FLEXIBLE_EVENT_CREATION,
  )

  // Stripe connection state
  const [showStripeConnectionModal, setShowStripeConnectionModal] =
    useState(false)
  const [isStripeLoading, setIsStripeLoading] = useState(false)

  const [email, setEmail] = useState(user?.email || "")
  const [legalName, setLegalName] = useState(user?.legalName || "")
  const [cooldown, setCooldown] = useState(0)
  const [isFormValid, setIsFormValid] = useState(false)
  const [dob, setDob] = useState<{ day: string; month: string; year: string }>({
    day: "",
    month: "",
    year: "",
  })
  const [verificationEmailSent, setVerificationEmailSent] = useState(false)
  const [emailVerified, setEmailVerified] = useState(
    user?.emailVerified || false,
  )

  const [isLoading, setIsLoading] = useState(false)
  const [profileImage, setProfileImage] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string>(user?.avatarUrl || "")

  // Use consolidated email verification
  const { verifyEmail, isLoading: emailVerificationLoading } =
    useEmailVerification()

  const dobRefs = {
    day: useRef<HTMLInputElement>(null),
    month: useRef<HTMLInputElement>(null),
    year: useRef<HTMLInputElement>(null),
  }

  useEffect(() => {
    if (user?.emailVerified) {
      setEmailVerified(true)
    }
  }, [user])

  useEffect(() => {
    if (user?.dateOfBirth) {
      const date = moment(user.dateOfBirth)

      setDob({
        day: date.format("DD"),
        month: date.format("MM"),
        year: date.format("YYYY"),
      })
    }
  }, [user])
  const countryCode = get(user, "country", "US")

  const fetchStripeUrlSilently = async () => {
    console.log("🔵 fetchStripeUrlSilently: Starting silent fetch")

    if (stripeUrl.isLoading) {
      console.log("🟡 fetchStripeUrlSilently: Already fetching, skipping")
      return
    }

    try {
      console.log("🔵 fetchStripeUrlSilently: Setting fetch state to true")
      setStripeUrl((prev) => ({ ...prev, isLoading: true }))

      const currentUrl = new URL(window.location.href)
      currentUrl.searchParams.set("redirectFrom", "stripe")
      const returnURL = currentUrl.toString()

      console.log(
        "🔵 fetchStripeUrlSilently: Calling onboardUserToStripe with:",
        {
          countryCode,
          returnURL,
        },
      )

      const response = await onboardUserToStripe({
        countryCode: countryCode,
        returnURL: returnURL,
        refreshURL: returnURL,
      })

      console.log("🔵 fetchStripeUrlSilently: API response:", response)

      const isSuccess = get(response, "success", true)
      console.log("🔵 fetchStripeUrlSilently: Success status:", isSuccess)

      if (!isSuccess) {
        console.log("🟡 fetchStripeUrlSilently: API returned failure, stopping")
        return
      }

      const error = get(response, "error", null)
      if (error) {
        console.log(
          "🟡 fetchStripeUrlSilently: API returned error, stopping:",
          error,
        )
        return
      }

      const url = get(response, "url", "")
      console.log("🔵 fetchStripeUrlSilently: Got Stripe URL:", url)

      if (url) {
        console.log("🟢 fetchStripeUrlSilently: Setting Stripe URL in state")
        setStripeUrl((prev) => ({ isLoading: false, url }))
      } else {
        console.log("🟡 fetchStripeUrlSilently: No URL received")
      }
    } catch (e) {
      console.error(
        "❌ fetchStripeUrlSilently: Silent stripe URL fetch failed:",
        e,
      )
      setStripeUrl((prev) => ({ isLoading: false, url: "" }))
    }
  }
  useEffect(() => {
    if (user?.stripeAccountId) return // No need to poll if already connected

    const intervalId = setInterval(
      () => {
        console.log("Running periodic check for Stripe URL")
        fetchStripeUrlSilently()
      },
      4 * 60 * 1000,
    ) // 4 minutes in milliseconds

    // Run once immediately as well
    fetchStripeUrlSilently()

    return () => clearInterval(intervalId) // Cleanup
  }, [])

  const pollVerificationStatus = async () => {
    try {
      const result = await getUserInfo({})
      if (result?.userInfo?.emailVerified) {
        setEmailVerified(true)

        setUser((prev) => {
          return {
            ...prev,
            ...result?.userInfo,
          }
        })
      }
    } catch (error) {
      console.error("Failed to poll verification status:", error)
    }
  }
  // Polling for email verification status
  useEffect(() => {
    if (!verificationEmailSent) return

    const intervalId = setInterval(pollVerificationStatus, 3000)

    return () => clearInterval(intervalId)
  }, [setUser, verificationEmailSent])

  const validateForm = () => {
    if (!email || !email.trim()) return false
    if (!emailVerified) return false
    if (!legalName || !legalName.trim()) return false
    if (!dob.day || !dob.month || !dob.year) return false
    const dobDate = moment(`${dob.year}-${dob.month}-${dob.day}`)
    if (!dobDate.isValid()) return false
    return true
  }

  useEffect(() => {
    setIsFormValid(validateForm())
  }, [email, verificationEmailSent, legalName, dob])

  const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setProfileImage(file)
      const objectUrl = URL.createObjectURL(file)
      setPreviewUrl(objectUrl)
    }
  }

  const handleSendVerificationEmail = async () => {
    if (!email || cooldown > 0) return

    const result = await verifyEmail(email, {
      onSuccess: () => {
        setVerificationEmailSent(true)
      },
      onCloseUI: () => {
        // Dev mode: immediate verification, update local state
        setEmailVerified(true)
        setVerificationEmailSent(true)
      },
      onRequireInboxCheck: () => {
        // Production mode: start cooldown and polling
        setVerificationEmailSent(true)
        setCooldown(30)
        const timer = setInterval(() => {
          setCooldown((prev) => {
            if (prev <= 1) {
              clearInterval(timer)
              return 0
            }
            return prev - 1
          })
        }, 1000)

        // Start polling for verification (already handled by the hook)
      },
      onVerificationComplete: () => {
        // Update local state when verification completes
        setEmailVerified(true)
      },
    })

    return result
  }

  const validateAndShowErrors = () => {
    if (!email || !email.trim()) {
      showErrorToast("Please enter a valid email")
      return false
    }
    if (!emailVerified) {
      showErrorToast("You need to verify your email first")
      return false
    }
    if (!legalName || !legalName.trim()) {
      showErrorToast("Please enter your name")
      return false
    }
    if (!dob.day || !dob.month || !dob.year) {
      showErrorToast("Please enter your Date of Birth")
      return false
    }
    const dobDate = moment(`${dob.year}-${dob.month}-${dob.day}`)
    if (!dobDate.isValid()) {
      showErrorToast("Invalid Date of Birth")
      return false
    }
    return true
  }

  const handleContinue = async () => {
    if (!validateAndShowErrors()) return
    setIsLoading(true)
    try {
      let avatarUrl = user?.avatarUrl
      if (profileImage) {
        const reader = new FileReader()
        const base64String = await new Promise<string>((resolve) => {
          reader.onload = () => resolve(reader.result as string)
          reader.readAsDataURL(profileImage)
        })
        avatarUrl = await uploadImage(base64String, "profile.png", user)
      }

      const formattedDOB = moment(`${dob.year}-${dob.month}-${dob.day}`).format(
        "YYYY-MM-DD",
      )

      const result = await updateUserDetails({
        ...user,
        name: user?.displayName || user?.name,
        legalName: legalName,
        dateOfBirth: formattedDOB,
        email,
        avatarUrl,
      })

      if (result?.success) {
        showSuccessToast("Profile updated successfully!")
        // pollVerificationStatus(true)
        setShowStripeConnectionModal(true)
        // return true
      } else {
        showErrorToast(result?.message || "Failed to update profile")
      }
    } catch (error) {
      showErrorToast("Failed to update profile")
    } finally {
      setIsLoading(false)
    }
  }

  // console.log("user", user)

  // Stripe connection functions
  const validateStripeConnection = () => {
    if (!email || !email.trim()) {
      showErrorToast("Please enter a valid email first")
      return false
    }
    if (!emailVerified) {
      showErrorToast("Please verify your email first")
      return false
    }
    if (!legalName || !legalName.trim()) {
      showErrorToast("Please enter your legal name first")
      return false
    }
    if (!dob.day || !dob.month || !dob.year) {
      showErrorToast("Please enter your date of birth first")
      return false
    }
    const dobDate = moment(`${dob.year}-${dob.month}-${dob.day}`)
    if (!dobDate.isValid()) {
      showErrorToast("Please enter a valid date of birth first")
      return false
    }
    return true
  }

  // Check if Stripe connection is enabled (computed value)
  const isStripeConnectionEnabled = () => {
    return (
      !!email?.trim() &&
      emailVerified &&
      !!legalName?.trim() &&
      !!dob.day &&
      !!dob.month &&
      !!dob.year &&
      moment(`${dob.year}-${dob.month}-${dob.day}`).isValid()
    )
  }

  const onConnectToStripe = async () => {
    if (!validateStripeConnection()) {
      return
    }

    console.log("🔵 onConnectToStripe: Starting Stripe connection flow")

    if (stripeUrl.url) {
      console.log(
        "🟢 onConnectToStripe: Using existing Stripe URL:",
        stripeUrl.url,
      )
      window.open(stripeUrl.url, "_self")
      setStripeUrl((prev) => ({ ...prev, url: "" }))
      return
    }

    console.log("🟡 onConnectToStripe: No existing URL, fetching new one...")

    try {
      setIsStripeLoading(true)

      const currentUrl = new URL(window.location.href)
      currentUrl.searchParams.set("redirectFrom", "stripe")
      const returnURL = currentUrl.toString()

      console.log("🔵 onConnectToStripe: Calling onboardUserToStripe API...")
      const response = await onboardUserToStripe({
        countryCode: "US", // Default to US or get from user if available
        returnURL: returnURL,
        refreshURL: returnURL,
      })

      console.log("🔵 onConnectToStripe: API response received:", response)

      const isSuccess = get(response, "success", true)
      if (!isSuccess) {
        const errorMessage = get(
          response,
          "message",
          "Failed to connect to Stripe",
        )
        showErrorToast(errorMessage)
        return
      }

      const error = get(response, "error", null)
      if (error) {
        showErrorToast("Something went wrong while connecting to Stripe")
        return
      }

      const url = get(response, "url", "")
      if (!url) {
        showErrorToast("Unable to get Stripe connection URL")
        return
      }

      console.log("🟢 onConnectToStripe: Redirecting to Stripe URL:", url)
      window.open(url, "_self")
    } catch (error) {
      console.error("Error connecting to Stripe:", error)
      showErrorToast("Failed to connect to Stripe")
    } finally {
      setIsStripeLoading(false)
    }
  }

  return (
    <BackgroundWrapper bgImage={backgroundImage} className="min-h-screen">
      <BlurWrapper
        blurKey={get(blurKeys, "mainBlurView")}
        overlayOpacityDefault="75"
        overlayColorDefault="dark"
        className="bg-gray-400/10 h-[10vh] flex flex-row items-center px-6 top-0 left-0 w-full z-10 fixed"
      >
        <div
          className="text-white text-base cursor-pointer bg-white/10 h-12 w-12 rounded-full flex justify-center items-center"
          onClick={() => {
            if (eventId) {
              navigate(`${ROUTES.EVENTS.replace(":eventId", eventId)}`, {
                replace: true, // Replace the current history entry
              })
            } else {
              navigate(ROUTES.LANDING)
            }
          }}
        >
          <IoChevronBack />
        </div>
        <div className="text-white text-base cursor-pointer flex-1 text-center">
          Complete your profile
        </div>
      </BlurWrapper>

      <BlurWrapper
        blurKey={get(blurKeys, "mainBlurView")}
        overlayOpacityDefault="75"
        overlayColorDefault="dark"
        className="min-h-screen flex flex-col items-center pt-[30%] md:pt-0 md:justify-center relative px-6"
      >
        {isLoading && <LoadingSpinner />}

        {/* Avatar */}
        <div className="relative mb-10 max-w-md w-full">
          <div className="relative w-24 h-24 flex items-center justify-center">
            <div className="w-full h-full rounded-full bg-white/20 overflow-hidden shadow-lg">
              {previewUrl ? (
                <img
                  src={previewUrl}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <FaUserCircle size={100} color="white" />
              )}
            </div>
            <label className="absolute bottom-0 right-0 aspect-square p-2 rounded-full bg-white/20 flex items-center justify-center cursor-pointer backdrop-blur-sm shadow-lg">
              <BiPencil size={20} color="white" />
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleProfileImageChange}
              />
            </label>
          </div>
        </div>

        <div className="w-full max-w-md text-left mb-6">
          <h3 className="text-white text-xl font-semibold mb-4">
            Your profile
          </h3>
          {/* Email */}
          <div className="text-white mb-2">Email</div>
          <div className="flex gap-2 mb-2">
            <input
              type="email"
              placeholder="names@email.com"
              className="flex-1 rounded-lg bg-white/20 px-4 py-3 text-white placeholder-white/40 outline-none border-none"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button
              className="rounded-lg bg-white/20 px-10 py-3 text-white font-semibold disabled:opacity-50"
              disabled={!email || emailVerified || cooldown > 0}
              onClick={handleSendVerificationEmail}
            >
              {verificationEmailSent
                ? cooldown > 0
                  ? `Resend (${cooldown}s)`
                  : "Resend"
                : "Verify"}
            </button>
          </div>
          <div className="text-white/50 mb-4 px-1">
            {verificationEmailSent && !emailVerified && email
              ? "We've sent a verification link to your email. Just click it to confirm your account. Make sure to check your spam"
              : ""}
            <p className="text-white">
              {email && emailVerified && "Verified ✅"}
            </p>
          </div>
          {/* Legal Name */}
          <div className="text-white mb-2">Legal Name</div>
          <input
            type="text"
            placeholder="John Doe"
            className="w-full rounded-lg bg-white/20 px-4 py-3 text-white placeholder-white/40 outline-none border-none mb-4"
            value={legalName}
            onChange={(e) => setLegalName(e.target.value)}
          />
          {/* Date of Birth */}
          <div className="text-white mb-2">Date of birth</div>
          <div className="mb-4">
            <CustomDateController
              onChange={setDob}
              value={dob}
              refs={dobRefs}
              user={user}
              error={null}
              errorClassname=""
            />
          </div>

          {/* Stripe Connection Section */}
          {flexibleEventCreationEnabled && (
            <>
              <div className="text-white mb-2">Bank Account Connection</div>
              <div className="mb-4">
                <div className="w-full rounded-lg bg-white/20 px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="text-2xl">
                      {user?.stripeAccountId ? "✅" : "💳"}
                    </div>
                    <div className="flex flex-col">
                      <span className="text-white font-medium">
                        {user?.stripeAccountId
                          ? "Connected to Stripe"
                          : "Connect to Stripe"}
                      </span>
                      <span className="text-white/60 text-sm">
                        {user?.stripeAccountId
                          ? "Your bank account is connected and ready to receive payouts"
                          : "Connect your bank account to receive payouts from ticket sales"}
                      </span>
                    </div>
                  </div>
                  {!user?.stripeAccountId && (
                    <button
                      className={`px-4 py-2 rounded-lg text-white font-medium transition-colors ${
                        isStripeConnectionEnabled()
                          ? "bg-white/20 hover:bg-white/30 cursor-pointer"
                          : "bg-white/10 opacity-50 cursor-pointer"
                      }`}
                      onClick={async () => {
                        if (isStripeConnectionEnabled()) {
                          if (!user?.legalName || !user?.dateOfBirth) {
                            handleContinue()
                          } else {
                            setShowStripeConnectionModal(true)
                          }
                        } else {
                          // Show validation errors to explain why it's disabled
                          validateStripeConnection()
                        }
                      }}
                    >
                      {isStripeLoading ? "Loading..." : "Connect"}
                    </button>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* <Button
          variant="solid"
          className={`w-full max-w-md py-3 rounded-full text-lg font-semibold mt-2 bg-white/20 ${
            isFormValid ? "opacity-100" : "opacity-50"
          } cursor-pointer`}
          onClick={handleContinue}
        >
          Continue
        </Button> */}
      </BlurWrapper>

      {/* Stripe Connection Modal */}
      <StripeConnectionModal
        isOpen={showStripeConnectionModal}
        onClose={() => setShowStripeConnectionModal(false)}
        onConnectToStripe={onConnectToStripe}
      />
    </BackgroundWrapper>
  )
}

export default CompleteProfilePage
